// brandPalette.ts — a curated quick-pick palette for the project brand-colour
// field (ColorField). The hues span the telecom-operator brand spectrum common
// across the Middle East + Africa market (yellows, oranges, warm reds, magentas,
// purples, blues, teals, greens) so a sysadmin onboarding an operator finds a
// close swatch fast — while the free hex input handles an exact brand match.
//
// Deliberately GENERIC names, never operator names: a swatch is a colour, not a
// company. The set is intentionally broad, not minimal — "most of the market" is
// the goal (the architect's brief).
//
// The whole file is colour DATA — the hex strings are the swatch content the user
// picks, not styling tokens — so it carries the same no-raw-color exemption class
// as branding.ts / index.css / the theme fixtures.
/* eslint-disable local/no-raw-color -- brand palette DATA: hex values are the pickable swatch content, not styling tokens */

export interface BrandSwatch {
  /** #rrggbb (lowercased). */
  readonly hex: string;
  /** Generic colour name — the swatch's accessible name (never an operator name). */
  readonly name: string;
}

// Grouped light→dark within a hue family so the grid reads as a spectrum.
export const BRAND_PALETTE: readonly BrandSwatch[] = [
  // Yellow · gold
  { hex: '#ffcb05', name: 'Sunflower' },
  { hex: '#f5a800', name: 'Gold' },
  // Orange · tangerine
  { hex: '#ff7900', name: 'Orange' },
  { hex: '#f36f21', name: 'Tangerine' },
  // Red · crimson
  { hex: '#e4002b', name: 'Signal red' },
  { hex: '#ed1c24', name: 'Scarlet' },
  { hex: '#c8102e', name: 'Crimson' },
  // Magenta · pink
  { hex: '#ec008c', name: 'Magenta' },
  { hex: '#e6007e', name: 'Fuchsia' },
  { hex: '#d81b60', name: 'Rose' },
  // Purple · violet
  { hex: '#57068c', name: 'Royal purple' },
  { hex: '#6e2585', name: 'Grape' },
  { hex: '#4f008c', name: 'Aubergine' },
  { hex: '#7c3aed', name: 'Violet' },
  // Blue · indigo
  { hex: '#43469c', name: 'Indigo' },
  { hex: '#0033a0', name: 'Cobalt' },
  { hex: '#005aa9', name: 'Azure' },
  { hex: '#1d4ed8', name: 'Bright blue' },
  // Cyan · teal
  { hex: '#00a9ce', name: 'Cyan' },
  { hex: '#009ca6', name: 'Teal' },
  { hex: '#0e7490', name: 'Deep teal' },
  // Green · emerald
  { hex: '#009639', name: 'Green' },
  { hex: '#43b02a', name: 'Grass' },
  { hex: '#008a5e', name: 'Emerald' },
];

/** Normalize any accepted hex input to `#rrggbb` lowercase, or null if invalid. */
export function normalizeHex(input: string): string | null {
  const v = input.trim().toLowerCase();
  const m = /^#?([0-9a-f]{6})$/.exec(v);
  if (m) return `#${m[1]}`;
  // Accept #rgb shorthand → expand to #rrggbb.
  const s = /^#?([0-9a-f]{3})$/.exec(v);
  if (s) {
    const [r, g, b] = s[1]!.split('');
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
}
